import { Fragment, useEffect, useState } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Timer, Loader2, AlertTriangle } from "lucide-react";

const crc = (n) => `₡${Number(n || 0).toLocaleString("es-CR", { maximumFractionDigits: 2 })}`;

export const FrtDialog = ({ open, onOpenChange, modeloId, modeloNombre, pedidoInicial, onAgregar }) => {
  const [pedido, setPedido] = useState("");
  const [cargando, setCargando] = useState(false);
  const [res, setRes] = useState(null);
  const [sel, setSel] = useState({});

  const consultar = async (texto) => {
    if (!texto.trim()) return;
    setCargando(true); setRes(null); setSel({});
    try {
      const { data } = await api.get("/frt/estimar", { params: { modelo_id: modeloId, q: texto } });
      setRes(data);
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setCargando(false); }
  };

  useEffect(() => {
    if (!open) { setRes(null); setSel({}); return; }
    setPedido(pedidoInicial || "");
    if (pedidoInicial?.trim()) consultar(pedidoInicial);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const marcadas = (res?.candidatas || []).filter((c) => sel[c.id]);
  const cands = res?.candidatas || [];
  const multiItem = new Set(cands.map((c) => c.item || "")).size > 1;

  const confirmar = () => {
    onAgregar(marcadas);
    toast.success(`${marcadas.length} estimado(s) FRT agregado(s) a la solicitud`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[88vh] flex flex-col p-4 sm:p-6" data-testid="frt-dialog">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex flex-wrap items-center gap-2">
            <Timer size={18} className="text-amber-600" /> Estimar mano de obra por FRT — {modeloNombre}
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700">Estimado, no plantilla</span>
          </DialogTitle>
        </DialogHeader>
        <div className="shrink-0 flex gap-2">
          <Input data-testid="frt-pedido-input" value={pedido} onChange={(e) => setPedido(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !cargando && consultar(pedido)}
            placeholder='Describa la operación (ej: "descansapie derecho del conductor")' />
          <Button data-testid="frt-consultar-btn" onClick={() => consultar(pedido)}
            disabled={cargando || !pedido.trim()} className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white">
            {cargando ? <Loader2 size={15} className="animate-spin" /> : "Buscar FRT"}
          </Button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain mt-3 space-y-2">
          {res?.sin_grupo && (
            <div className="flex items-start gap-2 rounded-sm bg-pink-50 border border-pink-200 px-3 py-2.5 text-sm text-pink-800"
              data-testid="frt-sin-grupo">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" /> {res.mensaje}
            </div>
          )}
          {res && !res.sin_grupo && (
            <>
              <div className="rounded-sm bg-secondary px-3 py-2 text-xs text-slate-600" data-testid="frt-tarifa-info">
                Tarifa: <strong>{crc(res.tarifa_hora)}/h</strong> (grupo "{res.grupo_tarifa}")
                {res.alcance_preferido && <> · Alcance detectado: <strong>{res.alcance_preferido}</strong></>}
              </div>
              {res.candidatas.length === 0 && (
                <p className="py-4 text-center text-sm text-slate-400" data-testid="frt-vacio">
                  Sin operaciones FRT que coincidan para este modelo. Pruebe con otras palabras.
                </p>
              )}
              {res.candidatas.length > 0 && (
                <p className="text-xs text-slate-400">Marque la operación correcta — ninguna viene seleccionada.</p>
              )}
              {res.candidatas.map((c, i) => (
                <Fragment key={c.id}>
                {multiItem && cands.findIndex((x) => (x.item || "") === (c.item || "")) === i && (
                  <p data-testid={`frt-item-header-${i}`}
                    className="mt-3 mb-1 text-[11px] font-bold uppercase tracking-wide text-amber-600 border-b border-amber-100 pb-1">
                    {c.item}
                  </p>
                )}
                <label data-testid={`frt-candidata-${i}`}
                  className={`flex items-start gap-2.5 rounded-lg border p-3 cursor-pointer transition-colors ${
                    sel[c.id] ? "border-amber-300 bg-amber-50/50" : "border-slate-200"}`}>
                  <Checkbox checked={!!sel[c.id]} onCheckedChange={(v) => setSel({ ...sel, [c.id]: !!v })}
                    data-testid={`frt-check-${i}`} className="mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-slate-800">{c.operacion}</span>
                      {c.alcance && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{c.alcance}</span>}
                      {c.seccion && <span className="rounded bg-sky-50 border border-sky-200 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700" data-testid={`frt-seccion-${i}`}>{c.seccion}</span>}
                      {c.preferida && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700" data-testid={`frt-preferida-${i}`}>Más probable</span>}
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500" data-testid={`frt-desglose-${i}`}>
                      {c.frt_horas} h × {crc(res.tarifa_hora)}/h = <strong className="text-slate-800">{crc(c.total)}</strong>
                    </p>
                    {c.razon && <p className="mt-0.5 text-[11px] text-slate-400">{c.razon}</p>}
                    {c.incluye && <p className="text-[11px] text-teal-700">Ya incluye: {c.incluye} (no cobrar aparte)</p>}
                    {c.operacion_original && <p className="text-[10px] text-slate-400">{c.operacion_original}</p>}
                  </div>
                </label>
                </Fragment>
              ))}
              {marcadas.length > 1 && (
                <div className="flex items-start gap-2 rounded-sm bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="frt-aviso-suma">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                  Sumar FRT de operaciones distintas puede sobreestimar (comparten desarme). Total: <strong>{crc(marcadas.reduce((a, c) => a + c.total, 0))}</strong> — ajuste si es necesario.
                </div>
              )}
              <p className="text-[11px] text-slate-400" data-testid="frt-nota">{res.nota}</p>
            </>
          )}
        </div>
        <div className="shrink-0 flex justify-end gap-2 pt-3 border-t border-slate-100">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="frt-confirmar-btn" onClick={confirmar} disabled={marcadas.length === 0}
            className="bg-amber-600 hover:bg-amber-700 text-white">
            Agregar estimado ({marcadas.length})
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
